<script setup lang="ts">
import { computed, ref, onMounted } from 'vue';

import { i18n } from '@apps/main/modules/localization/localizationSetup';
import { useRecipeStore } from '@apps/main/new-desktop/core/stores/recipe/recipeStore';
import {
  useGlobalCurrencyStore,
  useItineraryStore,
} from '@apps/main/new-desktop/core/stores';

import { AppExpandableButton, AppSvgIcon } from '@libs/ui';
import { DateFormat, SemboDate } from '@libs/sembo-dates';

import { useActivityDetailsTicketsStore } from '../../stores/activityDetailsTicketsStore';
import { ActivityDetailsTemplateId } from '../../models';
import { useActivityDetailsStore } from '../../stores';

import ActivityDetailsFooter from './ActivityDetailsFooter.vue';
import Ticket from './Ticket.vue';

const activityDetailsTicketsStore = useActivityDetailsTicketsStore();
const recipeStore = useRecipeStore();
const activityDetailsStore = useActivityDetailsStore();
const globalCurrencyStore = useGlobalCurrencyStore();
const itineraryStore = useItineraryStore();

const { t } = i18n.global;

interface TicketsSelectorMobileEmits {
  (event: 'close'): void;
  (event: 'select-tickets'): void;
}
const emit = defineEmits<TicketsSelectorMobileEmits>();

interface Header {
  title: string;
  date: Date;
  subtitle?: string;
}

const expandedButtonIndex = ref<number | null>(null);

const labels = computed<Header[]>(
  () =>
    activityDetailsTicketsStore.bookableDays?.map((date, index) => {
      const lowestPrice = activityDetailsTicketsStore.getTicketsLowestPrice(
        new Date(date)
      );
      return {
        title:
          itineraryStore.userSelectedItinerary === undefined
            ? `${t('planner.days')}  ${index + 1}`
            : new SemboDate(date).format(DateFormat.DateWithFullMonthComma),
        date: new Date(date),
        subtitle: lowestPrice
          ? t('planner.from_price_per_person', {
              price: globalCurrencyStore.formatMoney(
                lowestPrice.amount,
                lowestPrice.currency
              ),
            }).replace('/', '')
          : undefined,
      };
    }) ?? []
);

function handleButtonClick(
  index: number,
  date: Date,
  newExpandedState: boolean
): void {
  if (newExpandedState) {
    expandedButtonIndex.value = index;
    activityDetailsTicketsStore.selectDate(date);
  } else {
    expandedButtonIndex.value = null;
  }
}

onMounted(() => {
  if (labels.value.length === 1) {
    expandedButtonIndex.value = 0;
    if (labels.value[0]) {
      activityDetailsTicketsStore.selectDate(labels.value[0].date);
    }
  }
});
</script>

<template>
  <div class="tickets-selector-mobile">
    <div class="content-wrapper">
      <div class="activities-dates-wrapper">
        <AppExpandableButton
          v-for="(header, titleIndex) in labels"
          :key="titleIndex"
          class="single-date-selector"
          :expanded="expandedButtonIndex === titleIndex"
          @expanded="handleButtonClick(titleIndex, header.date, $event)"
        >
          <template #left-icon>
            <AppSvgIcon size="18" name="calendarOutlined" />
          </template>
          <template #title>
            <span>{{ header.title }}</span>
          </template>
          <template #subtitle>
            <span v-if="header.subtitle" class="price-subtitle">
              {{ header.subtitle }}
            </span>
          </template>
          <template #content>
            <div
              v-if="
                activityDetailsTicketsStore.selectedDateOffset &&
                activityDetailsStore.activityStaticData?.productCode &&
                activityDetailsStore.activityStaticData?.staticInfo.typeOfHotel
              "
              :id="ActivityDetailsTemplateId.TicketRates"
              class="tickets-container"
            >
              <Ticket
                v-for="(
                  ticketRate, index
                ) in activityDetailsTicketsStore.ticketRatesInSelectedDate"
                :key="
                  ticketRate.rateId +
                  activityDetailsTicketsStore.selectedDateOffset
                "
                :ticket-rate="ticketRate"
                :product-code="
                  activityDetailsStore.activityStaticData.productCode
                "
                :date-offset="activityDetailsTicketsStore.selectedDateOffset"
                :initial-rate-tickets="
                  recipeStore.getSelectedTicketsForRate(
                    ticketRate.rateId,
                    activityDetailsTicketsStore.selectedDateOffset,
                    activityDetailsStore.activityStaticData?.isAlternative
                  )
                "
                :activity-type="
                  activityDetailsStore.activityStaticData.staticInfo.typeOfHotel
                "
                @update-selected-status="
                  activityDetailsTicketsStore.updateTicketRateValidityStatus(
                    $event,
                    index
                  )
                "
              />
            </div>
          </template>
        </AppExpandableButton>
      </div>
    </div>
    <div class="footer-wrapper">
      <ActivityDetailsFooter
        @close="emit('close')"
        @select-tickets="emit('select-tickets')"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.tickets-selector-mobile {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;

  .content-wrapper {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    .activities-dates-wrapper {
      padding: var(--spacing-16);
      display: flex;
      flex-direction: column;
      gap: var(--spacing-16);

      .single-date-selector {
        padding: var(--spacing-16);
        border-radius: var(--border-radius-8);
        border: 1px solid var(--color-neutral-30);
      }
      .tickets-container {
        display: flex;
        flex-direction: column;
        padding-top: var(--spacing-16);
        gap: var(--spacing-16);
      }
    }
  }

  .footer-wrapper {
    flex-shrink: 0;
    background-color: var(--color-white);
    border-top: 1px solid var(--color-scrollbar);
  }
}
</style>
